/**
 * Textarea + send button + Context bar (Context label, attached chips,
 * `+ Add` pill that opens the reference picker). The `@` shortcut in
 * the textarea ALSO opens the picker — both paths share the same
 * `ReferencePicker` so the candidate set, keyboard nav, and tick
 * states stay in sync.
 *
 * Session context tags are owned by the parent (`useChat` runtime
 * store): they default to the open workshop, survive across messages,
 * and only change when the user adds/removes chips.
 *
 * Visual chrome ports the legacy markup verbatim:
 *   - Outer `.chat-input-area` (xaes.css:803) — border-top + padding.
 *   - Context bar row above the input — muted "Context" label, inline
 *     chips, true-blue tinted `+ Add` pill (wd.html:649-674).
 *   - `.chat-input-box` (xaes.css:804) — sky-bordered, bg-panel-alt
 *     rounded 8 px container, holding the textarea + a sibling pair
 *     of round `.chat-add-btn` (+) and rounded `.chat-send-btn` (➤).
 *     The textarea’s scrollbar uses the legacy thin true-blue thumb
 *     over a dim cool track (xaes.css:805 + wd.html:677-683 inline).
 *
 * The input-box `+` is a no-op stub (focuses the textarea); legacy
 * reserved it as a future file-attachment trigger. The reference
 * picker is anchored to the `+ Add` pill in the context bar instead.
 */

import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Plus, Send } from "lucide-react";
import type { XaesReference } from "../reference-resolver";
import type { ChatScope } from "../context-builder";
import type { ChatStatus } from "../use-chat";
import { ContextChips, refKey } from "./ContextChips";
import { ReferencePicker } from "./ReferencePicker";

type Props = {
  scope: ChatScope;
  status: ChatStatus;
  /** Session-level context tags — persist across messages. */
  references: XaesReference[];
  onReferencesChange: (references: XaesReference[]) => void;
  /** Disables the input when the parent says so (mock-mode lockdown etc). */
  disabled?: boolean;
  onSend: (text: string, references: XaesReference[]) => void;
  /**
   * Kept in the public surface so the parent (`XaesAnalyst`) doesn't have
   * to change its prop wiring. The redesigned chrome simply disables the
   * send button while busy rather than morphing it into a stop button, so
   * `onAbort` is no longer surfaced inside this component — `use-chat`
   * still owns the abort behaviour internally.
   */
  onAbort?: () => void;
};

export function ChatInput({
  scope,
  status,
  references,
  onReferencesChange,
  disabled,
  onSend,
}: Props) {
  const [text, setText] = useState("");
  // Two independent open states: `@`-triggered (anchored to the
  // textarea) vs `+ Add`-triggered (anchored to the pill). Keeping
  // them separate means clicking +Add doesn't move the popover under
  // the textarea and vice-versa.
  const [inlinePickerOpen, setInlinePickerOpen] = useState(false);
  const [barPickerOpen, setBarPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  // Index of the `@` that opened the picker — lets us compute the
  // filter from text *after* it and clean up on selection.
  const triggerIdxRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const busy = status === "thinking" || status === "streaming";
  const canSend = text.trim().length > 0 && !busy && !disabled;

  const activeKeys = useMemo(
    () => new Set(references.map(refKey)),
    [references],
  );

  function commitSend(): void {
    if (!canSend) return;
    onSend(text.trim(), references);
    setText("");
    // Keep session context tags — they stay for the whole chat session.
    setInlinePickerOpen(false);
    setBarPickerOpen(false);
    triggerIdxRef.current = null;
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (inlinePickerOpen) {
      // Let the picker's arrow/enter/escape handler take precedence.
      if (
        e.key === "Enter" ||
        e.key === "Escape" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowUp"
      ) {
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitSend();
      return;
    }
    if (e.key === "@") {
      // Only trigger at start-of-input or after whitespace so we don't
      // hijack `@` in email addresses pasted into the textarea.
      const el = e.currentTarget;
      const caret = el.selectionStart ?? el.value.length;
      const prevChar = caret > 0 ? el.value[caret - 1] : "";
      if (!prevChar || /\s/.test(prevChar)) {
        triggerIdxRef.current = caret;
        setInlinePickerOpen(true);
        setPickerQuery("");
      }
    }
  }

  function handleChange(value: string): void {
    setText(value);
    if (!inlinePickerOpen) return;
    const at = triggerIdxRef.current;
    if (at === null || at >= value.length || value[at] !== "@") {
      setInlinePickerOpen(false);
      setPickerQuery("");
      triggerIdxRef.current = null;
      return;
    }
    setPickerQuery(value.slice(at + 1));
  }

  function addOrToggleRef(ref: XaesReference): void {
    const k = refKey(ref);
    const idx = references.findIndex((r) => refKey(r) === k);
    if (idx >= 0) {
      // Toggle off — matches the legacy dropdown behaviour where
      // clicking a checked row deselects it.
      onReferencesChange(references.filter((_, i) => i !== idx));
      return;
    }
    onReferencesChange([...references, ref]);
  }

  function handleInlinePick(ref: XaesReference): void {
    addOrToggleRef(ref);
    // Strip the @… fragment back to the next whitespace.
    const at = triggerIdxRef.current;
    setText((prev) => {
      if (at === null || at >= prev.length || prev[at] !== "@") return prev;
      const after = prev.slice(at + 1);
      const space = after.search(/\s|$/);
      const end = at + 1 + (space === -1 ? after.length : space);
      return prev.slice(0, at) + prev.slice(end);
    });
    setInlinePickerOpen(false);
    setPickerQuery("");
    triggerIdxRef.current = null;
    textareaRef.current?.focus();
  }

  function handleBarPick(ref: XaesReference): void {
    addOrToggleRef(ref);
    // Don't close — the bar picker is multi-select by design, mirroring
    // the legacy `#chatContextDropdown` which stayed open while the
    // user toggled multiple workshops.
  }

  function removeReference(ref: XaesReference): void {
    const target = refKey(ref);
    onReferencesChange(references.filter((r) => refKey(r) !== target));
  }

  return (
    <div className="chat-input-area">
      {/* ── Context bar — legacy `#chatContextBar` (wd.html:649-674) ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "0 0 4px 0",
          flexWrap: "wrap",
          position: "relative",
        }}
      >
        <span
          style={{
            flexShrink: 0,
            fontFamily: "Inter, sans-serif",
            fontSize: "var(--fs-label)",
            fontWeight: 700,
            letterSpacing: "2px",
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          Context
        </span>

        <ContextChips references={references} onRemove={removeReference} />

        <ReferencePicker
          scope={scope}
          query=""
          open={barPickerOpen}
          onOpenChange={setBarPickerOpen}
          onSelect={handleBarPick}
          activeKeys={activeKeys}
          side="top"
          // +Add stays workshops-only to match the legacy
          // `#chatContextDropdown` (`_legacy/wd.html` lines 1996-2005),
          // which surfaced exactly the 8 workshop rows. Richer kinds
          // (phase / output / asset) remain reachable via the @-typed
          // inline picker below.
          kindFilter={["workshop"]}
        >
          <button
            type="button"
            onClick={() => {
              triggerIdxRef.current = null;
              setPickerQuery("");
              setInlinePickerOpen(false);
              setBarPickerOpen((v) => !v);
            }}
            className="flex-shrink-0 clickable"
            aria-label="Add context reference"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 9px",
              borderRadius: 20,
              background: "rgba(var(--accent-true-blue-rgb), 0.06)",
              border: barPickerOpen
                ? "1px solid rgba(var(--accent-true-blue-rgb), 0.45)"
                : "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
              color: barPickerOpen
                ? "var(--accent-true-blue)"
                : "var(--text-muted)",
              fontFamily: "Inter, sans-serif",
              fontSize: "var(--fs-label)",
              fontWeight: 600,
              letterSpacing: "1px",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.borderColor =
                "rgba(var(--accent-true-blue-rgb), 0.45)";
              el.style.color = "var(--accent-true-blue)";
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.borderColor = barPickerOpen
                ? "rgba(var(--accent-true-blue-rgb), 0.45)"
                : "rgba(var(--accent-true-blue-rgb), 0.2)";
              el.style.color = barPickerOpen
                ? "var(--accent-true-blue)"
                : "var(--text-muted)";
            }}
          >
            <Plus
              aria-hidden
              style={{ width: 11, height: 11 }}
              strokeWidth={2.5}
            />
            Add
          </button>
        </ReferencePicker>
      </div>

      {/* ── Input box — legacy `.chat-input-box` (xaes.css:804). Legacy
          `wd.html:677` overrides padding to 0 inline because the
          textarea owns its own 7×10 inset; the box just supplies the
          sky border + 8 px radius. */}
      <div
        className="chat-input-box"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          padding: 0,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {/* Invisible inline-picker anchor — sits at the start of the
            input row so the `@`-triggered popover floats above the
            caret area rather than under the +Add pill. */}
        <ReferencePicker
          scope={scope}
          query={pickerQuery}
          open={inlinePickerOpen}
          onOpenChange={setInlinePickerOpen}
          onSelect={handleInlinePick}
          activeKeys={activeKeys}
          side="top"
        >
          <span
            aria-hidden
            style={{
              width: 1,
              height: 1,
              alignSelf: "stretch",
              display: "block",
              flexShrink: 0,
              pointerEvents: "none",
            }}
          />
        </ReferencePicker>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            inlinePickerOpen
              ? "Type to filter references…"
              : "How can I help you today?"
          }
          disabled={disabled}
          rows={1}
          style={{
            flex: 1,
            padding: "7px 10px",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginRight: 6,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            className="chat-add-btn"
            // TODO: hook up file-attachment picker — legacy reserved
            // this `+` button as the entry point for future asset
            // uploads. For now it just focuses the textarea.
            onClick={() => textareaRef.current?.focus()}
            aria-label="Attach (coming soon)"
          >
            <Plus aria-hidden style={{ width: 14, height: 14 }} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className="chat-send-btn"
            onClick={commitSend}
            disabled={!canSend}
            aria-label="Send message"
          >
            <Send aria-hidden style={{ width: 14, height: 14 }} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
